import { computed, type ComputedRef } from "vue";
import { storeToRefs } from "pinia";
import { useSurveyStore } from "@stores/survey/survey";

export interface RecodeTypeItem {
  key: string;
  name: string;
  description: string;
  about?: string;
  example?: string;
  aiOnly?: boolean;
  mailingOnly?: boolean;
  analysisOnly?: boolean;
}

export const RECODE_ITEMS = [
  { key: "group_answers", name: "Group answers", description: "Answers grouped together", about: "Groups the answers of one question into broader answers.", example: "“18-24” and “25-34” become “Under 35”" },
  { key: "conditions", name: "Logic conditions", description: "A value set by conditions", about: "Sets a value from conditions on one or more questions.", example: "“Loyal” if Q4 is 3 or more and Q5 is “Yes”" },
  { key: "geo", name: "Geo", description: "A zone from a postal code", about: "Turns a postal code into a geographic zone, such as a région or a département.", example: "69003 becomes “Auvergne-Rhône-Alpes”" },
  { key: "url_param", name: "URL parameter", description: "A value from the survey link", about: "Reads a parameter from the survey link and stores it as an answer.", example: "?source=newsletter becomes “newsletter”" },
  { key: "mailing", name: "Mailing list attribute", description: "A value from the mailing list", about: "Reads an attribute from the mailing list the respondent was invited from.", example: "The contact’s “store” becomes “Lyon Part-Dieu”", mailingOnly: true },
  { key: "open_end", name: "Open end codification", description: "Text answers sorted into categories", about: "Sorts the answers of an open question into categories, generated with AI or added by hand.", example: "“Too many steps before paying” goes to “Checkout”", analysisOnly: true },
  { key: "interview_codification", name: "AI recode (interviews)", description: "Interviews coded by AI", about: "Codes AI interviews into categories, or writes a value for each respondent from what they said.", example: "“I ride a folding Brompton” gives “Folding bike”", aiOnly: true, analysisOnly: true },
] as const satisfies readonly RecodeTypeItem[];

// Derived from RECODE_ITEMS so the list stays the single source of truth.
export type RecodeType = (typeof RECODE_ITEMS)[number]["key"];

export function useRecodeTypeItems(): { availableRecodeItems: ComputedRef<RecodeTypeItem[]> } {
  const surveyStore = useSurveyStore();
  const { isAiChatSurvey, hasMailingAccess } = storeToRefs(surveyStore);

  // Capability-gated list (AI recode only for AI-chat surveys, mailing only with access).
  const availableRecodeItems = computed(() =>
    RECODE_ITEMS.filter((item: RecodeTypeItem) => {
      if (item.aiOnly && !isAiChatSurvey.value) return false;
      if (item.mailingOnly && !hasMailingAccess.value) return false;
      return true;
    }),
  );

  return { availableRecodeItems };
}
